import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

// MUI
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { DataGrid } from "@mui/x-data-grid";
import apiClient from '../ApiClient/ApiClient';

function QuoteLookup({ kind, value, onChange, label }) {
  const text = (option) => kind === "item"
    ? `${option.drawNum}${option.itemName ? `／${option.itemName}` : ""}`
    : `${option.code || ""} ${option.name}${option.fullName ? `／${option.fullName}` : ""}`.trim();
  const [search, setSearch] = useState("");
  const [input, setInput] = useState("");
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Searching a selected value uses its key rather than the combined display label.
  useEffect(() => {
    if (value) {
      setSearch(kind === "item" ? value.drawNum : value.name);
      setInput(kind === "item" ? `${value.drawNum}${value.itemName ? `／${value.itemName}` : ""}`
        : `${value.code || ""} ${value.name}${value.fullName ? `／${value.fullName}` : ""}`.trim());
    }
  }, [kind, value]);
  useEffect(() => {
    const controller = new AbortController();
    setOptions([]);
    setError("");
    setBusy(true);
    const timer = setTimeout(async () => {
      try {
        const response = await apiClient.get('/PurchasePrice/QuoteOptions', {
          params: { kind, search }, signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const body = response.data;
        if (!(body.success ?? body.Success)) throw new Error(body.message ?? body.Message ?? "選項讀取失敗");
        const list = body.data ?? body.Data ?? [];
        setOptions(list);
        if (kind === "vendor" && value?.id === 0) {
          const matches = list.filter((x) => x.name === value.name);
          if (matches.length === 1) onChange(matches[0]);
        }
      } catch (e) {
        if (!controller.signal.aborted) setError(e.response?.data?.message ?? e.message ?? "選項讀取失敗");
      } finally {
        if (!controller.signal.aborted) setBusy(false);
      }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [kind, search, value, onChange]);
  return <Autocomplete
    options={value ? [value, ...options.filter((x) => kind === "item" ? x.drawNum !== value.drawNum : x.id !== value.id)] : options}
    value={value} inputValue={input} loading={busy} filterOptions={(list) => list}
    getOptionLabel={text}
    isOptionEqualToValue={(a, b) => kind === "item" ? a.drawNum === b.drawNum : a.id === b.id}
    onChange={(_, option) => { onChange(option); if (!option) { setInput(""); setSearch(""); } }}
    onInputChange={(_, next, reason) => { if (reason === "input") { setInput(next); setSearch(next); if (value) onChange(null); } }}
    noOptionsText={error || "查無選項，請輸入其他關鍵字"}
    renderOption={(props, option) => <li {...props} key={kind === "item" ? option.drawNum : option.id}>{text(option)}</li>}
    renderInput={(params) => <TextField {...params} label={label} size="small" required
      error={Boolean(error)} helperText={error || "輸入關鍵字搜尋，最多顯示50筆；請點選主檔資料"} />}
  />;
}

QuoteLookup.propTypes = {
  kind: PropTypes.oneOf(["item", "vendor"]).isRequired,
  value: PropTypes.shape({
    id: PropTypes.number, drawNum: PropTypes.string, itemName: PropTypes.string,
    code: PropTypes.string, name: PropTypes.string, fullName: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

/**
 * 改版重點：
 * 1) 取消模式切換，固定顯示：查詢區、廠商報價（主表 DataGrid）、進價紀錄（副表 DataGrid）。
 * 2) 上/下一頁按鈕移除，改由 DataGrid 呈現（一次載入目前查詢的所有列）。
 * 3) 「編輯」改為使用 MUI Dialog 小視窗。
 * 4) 匯出：保留「一覽表 Excel」(以查詢條件) 與「單一圖號調價紀錄 Excel」。
 */

export default function PurchasePrice({ data }) {
  // 查詢條件
  const [drawNum, setDrawNum] = useState("");
  const [itemName, setItemName] = useState("");
  const [company, setCompany] = useState("");
  const [startDrawNum, setStartDrawNum] = useState("");
  const [endDrawNum,   setEndDrawNum]   = useState("");
  const [pendingDetailEdit, setPendingDetailEdit] = useState(null);
  const [canEditDetail, setCanEditDetail] = useState(false);
  const [oldId, setOldId] = useState(0); // 預設 0（或你想要的數字）


  // 載入遮罩
  const [loading, setLoading] = useState(false);

  // 主表、細表資料
  const [mainRows, setMainRows] = useState([]); // PurchasePricesList
  const [detailRows, setDetailRows] = useState([]); // PurchaseLogsList

  // 目前選取的圖號（用來載入進價紀錄）
  const [selectedDrawNum, setSelectedDrawNum] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("");

  // 編輯對話框
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [quoteItem, setQuoteItem] = useState(null);
  const [quoteVendor, setQuoteVendor] = useState(null);
  const [canMaintainQuote, setCanMaintainQuote] = useState(false);
  const savingQuote = React.useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    apiClient.get('/PurchasePrice/QuotePermissions', { signal: controller.signal })
      .then((res) => { if (!controller.signal.aborted) setCanMaintainQuote((res.data.data ?? res.data.Data)?.canEdit === true); })
      .catch(() => { if (!controller.signal.aborted) setCanMaintainQuote(false); });
    return () => controller.abort();
  }, []);

  // 將舊有傳入 data（若父層有預先載入）轉為列
  useEffect(() => {
    if (!data) return;
    const list = normalizeMainList(data);
    setMainRows(list);
  }, [data]);

  // ------ 工具函式 ------
  function normStatus(res) {
    return res?.StatusCode ?? res?.statusCode;
  }
  function normalizeMainList(res) {
    const list = res?.PurchasePricesList ?? res?.purchasePricesList ?? [];
    return Array.isArray(list) ? list.map(attachRowIdFromMain) : [];
  }
  function normalizeDetailList(res) {
    const list = res?.PurchaseLogsList ?? res?.purchaseLogsList ?? [];
    return Array.isArray(list) ? list.map(attachRowIdFromDetail) : [];
  }
  function attachRowIdFromMain(x, idx) {
    // DataGrid 需要 id 欄位
    // 後端已有 x.id；若沒有就用索引備援
    return { ...x, id: x.id ?? `${x.drawNum}-${x.company}-${idx}` };
  }
  function attachRowIdFromDetail(x, idx) {
    return { ...x, id: x.id ?? `${x.drawNum}-${x.company}-${x.date}-${idx}` };
  }
  function currencySymbol(c) {
    if (!c) return "";
    switch (String(c).trim().toUpperCase()) {
      case "TWD":
        return "NT$";
      case "USD":
        return "USD$";
      case "CNY":
        return "CNY¥";
      case "PHP":
        return "PHP₱";
      case "HKD":
        return "HK$";
      case "AUD":
        return "A$";
      case "EUR":
        return "EUR€";
      default:
        return "";
    }
  }

  const endDrawNumRef = React.useRef(null);

  const commitStartToEnd = (val) => {
    setEndDrawNum(val);
    // 可選：把游標移到結束圖號，讓使用者直接微調
    endDrawNumRef.current?.focus();
  };

  // ------ API ------
  const fetchMain = async (override) => {
    const criteria = override?.drawNum ? override : { drawNum, endDrawNum, itemName, company };
    if (!criteria.drawNum && !criteria.itemName && !criteria.company) {
      alert("搜尋條件不能全空");
      return;
    }
    setLoading(true);
    try {
      const resp = await apiClient.get(
        `/PurchasePrice/GetMain`,
        { params: criteria }
      );
      const status = normStatus(resp.data);
      if (status === 200) {
        const list = normalizeMainList(resp.data);
        setMainRows(list);
        setDetailRows([]);
        setCanEditDetail(false);
        setSelectedDrawNum("");
        setSelectedVendor("");
        alert("搜尋成功");
      } else {
        setMainRows([]);
        setDetailRows([]);
        setSelectedDrawNum("");
        setSelectedVendor("");
        alert("查無資料");
      }
    } catch (err) {
      console.error(err);
      alert("搜尋失敗");
    } finally {
      setLoading(false);
    }
  };

  const fetchDetail = async (dn, vendor, sn) => {
    if(!sn) return;
    setOldId(sn);
    const normalizedDrawNum = String(dn ?? "").trim();
    const normalizedVendor = String(vendor ?? "").trim();
    if (!normalizedDrawNum || !normalizedVendor) return;
    setLoading(true);
    try {
      const resp = await apiClient.get(`/PurchasePrice/DetailList`, {
        params: { drawNum: normalizedDrawNum, vendor: normalizedVendor },
      });
      const status = normStatus(resp.data);
      if (status === 200) {
        const list = normalizeDetailList(resp.data);
        setDetailRows(list);
        setCanEditDetail(resp.data?.CanEdit ?? resp.data?.canEdit ?? false);
        setSelectedDrawNum(normalizedDrawNum);
        setSelectedVendor(normalizedVendor);
      } else {
        setSelectedDrawNum(normalizedDrawNum);
        setSelectedVendor(normalizedVendor);
        setDetailRows([]);
        setCanEditDetail(false);
      }
    } catch (err) {
      console.error(err);
      setCanEditDetail(false);
      alert("讀取進價紀錄失敗");
    } finally {
      setLoading(false);
    }
  };

  const deleteMain = async (row) => {
    if (!window.confirm(`確定要刪除廠商「${row.company}」的報價嗎？`)) return;
    setLoading(true);
    try {
      const payload = {
        drawNum: row.drawNum,
        company: row.company,
        price: row.price,
        engineering: row.engineering,
        id: row.id,
        currency: row.currency ?? "",
      };
      const resp = await apiClient.post(`/PurchasePrice/DeleteMain`, payload);
      if (resp?.data === 200) {
        // 重新查詢主表（以目前條件）
        await fetchMain();
        alert("刪除成功");
      } else {
        alert("刪除失敗，找不到可刪除物件");
      }
    } catch (err) {
      console.error(err);
      alert("刪除失敗，請稍後再試");
    } finally {
      setLoading(false);
    }
  };

  const deleteDetail = async (row) => {
    if (!window.confirm(`確定刪除此筆進價紀錄（${row.date} / ${row.company}）？`))
      return;
    setLoading(true);
    try {
      const payload = {
        drawNum: row.drawNum,
        company: row.company,
        date: row.date,
        pk: row.pk,
        price: row.price,
        pricePerson: row.pricePerson,
      };
      const resp = await apiClient.post(`/PurchasePrice/DeleteDetail`, payload);
      if (resp?.data === 200) {
        setDetailRows([]);
        await fetchDetail(row.drawNum, row.company, oldId);

        alert("刪除成功");
      } else {
        alert("刪除失敗，找不到可刪除物件");
      }
    } catch (err) {
      console.error(err);
      alert("刪除失敗，請稍後再試");
    } finally {
      setLoading(false);
    }
  };

  const exportSingleExcel = async () => {
    const dn = selectedDrawNum || drawNum;
    if (!dn) {
      alert("請先在上方輸入圖號，或點主表列載入進價紀錄");
      return;
    }
    setLoading(true);
    try {
      const resp = await apiClient.get(`/PurchasePrice/CreateExcel`, {
        params: { drawNum: dn },
        responseType: "blob",
      });
      if (resp?.data) {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        const filename = `進價調價紀錄_${dn}_${yyyy}${mm}${dd}.xlsx`;
        const url = window.URL.createObjectURL(new Blob([resp.data]));
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", filename);
        document.body.appendChild(a);
        a.click();
        a.remove();
        alert("匯出成功");
      } else {
        alert("匯出失敗");
      }
    } catch (err) {
      console.error(err);
      alert("匯出失敗");
    } finally {
      setLoading(false);
    }
  };

  // 放在 component 裡任何欄位定義之前：安全轉數字的小工具
const toNumberSafe = (v) => {
  if (v == null) return null;

  const s = String(v)
    .trim()
    .replace(/[，,]/g, "")
    .replace(/[。．]/g, ".")
    .replace(/[－—–]/g, "-");

  if (!/^-?\d+(\.\d+)?$/.test(s)) return null;

  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};


  const exportListExcel = async () => {
    if (!drawNum && !itemName && !company) {
      alert("至少輸入一個查詢條件");
      return;
    }
    setLoading(true);
    try {
      const resp = await apiClient.get(`/PurchasePrice/CreateExcel2`, {
        params: { drawNum, endDrawNum,itemName, company },
        responseType: "blob",
      });
      if (resp?.data) {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        const filename = `進價一覽表_${yyyy}${mm}${dd}.xlsx`;
        const url = window.URL.createObjectURL(new Blob([resp.data]));
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", filename);
        document.body.appendChild(a);
        a.click();
        a.remove();
        alert("匯出成功");
      } else {
        alert("匯出失敗");
      }
    } catch (err) {
      console.error(err);
      alert("匯出失敗");
    } finally {
      setLoading(false);
    }
  };

  // 右側進價紀錄：保存單列
  const saveEditedDetail = async () => {
    if (!pendingDetailEdit) return;
    setLoading(true);
    try {
      const parsedPrice = toNumberSafe(pendingDetailEdit.price);
      if (parsedPrice == null) {
        alert("進價格式不正確");
        setLoading(false);
        return;
      }

      const normalizedCurrency = String(pendingDetailEdit.curr ?? "").trim().toUpperCase();
      const allowedCurrencies = ["TWD", "USD", "PHP", "CNY", "HKD", "AUD", "EUR"];
      if (!allowedCurrencies.includes(normalizedCurrency)) {
        alert("請先設定幣別，再保存最新進價紀錄");
        setLoading(false);
        return;
      }
  
      const payload = {
        drawNum: pendingDetailEdit.drawNum,
        company: pendingDetailEdit.company,
        date: pendingDetailEdit.date,
        pk: oldId, // 廠商報價資料表的id
        price: parsedPrice,                             // ← 這裡用轉好的數字
        curr: normalizedCurrency,
        pricePerson: sessionStorage.getItem("userName") // 調價人員
      };
  
      const resp = await apiClient.post(`/PurchasePrice/EditDetail`, payload);
      if (resp?.status === 200) {
        alert("保存成功");
        setPendingDetailEdit(null);
        await fetchMain();
        await fetchDetail(pendingDetailEdit.drawNum, pendingDetailEdit.company, oldId);
      } else {
        alert("保存失敗");
      }
    } catch (err) {
      console.error(err);
      alert("保存失敗，請稍後再試");
    } finally {
      setLoading(false);
    }
  };
  


  // ------ DataGrid 欄位定義：主表 ------
  const mainColumns = useMemo(
    () => [
      { field: "drawNum", headerName: "圖號", minWidth: 140, flex: 1 },
      { field: "itemName", headerName: "物品名", minWidth: 220, flex: 1,
        renderCell: (p) => (
        <Box sx={{
          whiteSpace: "normal",     // 允許換行
          wordBreak: "break-word",  // 長字也能斷行
          lineHeight: 1.3,
          display: "block"          // 確保高度吃到多行
        }}>
      {p.value}
      </Box>
      ),
      },
      { field: "company", headerName: "廠商", minWidth: 90, flex: 1 },
      {
        field: "price",
        headerName: "進價",
        minWidth: 110,
        flex: 0.7,
        valueGetter: (p) => p.row.price,
        renderCell: (p) => (
          <span>{currencySymbol(p.row.currency)}{p.row.price}</span>
        ),
      },
      { field: "engineering", headerName: "工程內容", minWidth: 75, flex: 1 },
      { field: "currency", headerName: "幣別", minWidth: 60, flex: 0.5 },
      {
        field: "actions",
        headerName: "操作",
        sortable: false,
        filterable: false,
        minWidth: 260,
        flex: 0.9,
        renderCell: (p) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              color="success"
              onClick={() => fetchDetail(p.row.drawNum, p.row.company, p.row.id)}
            >
              進價紀錄
            </Button>
            {canMaintainQuote && <Button
              variant="contained"
              size="small"
              color="warning"
              onClick={() => {
                setEditItem(p.row);
                setQuoteItem({ drawNum: p.row.drawNum, itemName: p.row.itemName });
                setQuoteVendor({ id: 0, name: p.row.company });
                setEditOpen(true);
              }}
            >
              新增／調價
            </Button>}
            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={() => deleteMain(p.row)}
            >
              刪除
            </Button>
          </Box>
        ),
      },
    ],
    [canMaintainQuote]
  );

  // ------ DataGrid 欄位定義：副表（進價紀錄） ------
  const detailColumns = useMemo(
    () => [
      
      { field: "company", headerName: "廠商", minWidth: 60, flex: 1 },
      { field: "date", headerName: "日期", minWidth: 120, flex: 0.7 },
      // detailColumns 的 price 欄位改成這樣
{
  field: "price",
  headerName: "進價",
  minWidth: 90,
  flex: 0.6,
  type: "string",         // ★ 視為字串編輯，避免內建數字編輯攔截 '.'
  editable: canEditDetail,

  // 顯示：前方帶幣別。優先用 curr，退而求其次用 currency
  valueFormatter: (p) => {
    const r = p.api.getRow(p.id);
    const sym = currencySymbol(r?.curr || r?.currency);
    return p.value == null ? "" : `${sym}${p.value}`;
  },

  // 編輯：用 TextField 接管，允許輸入 0.、0.36 等
  renderEditCell: (params) => {
    const { id, field, value, api } = params;
    const handleChange = (e) => {
      api.setEditCellValue({ id, field, value: e.target.value }, e);
    };
    return (
      <TextField
        size="small"
        type="text"
        autoFocus
        value={value ?? ""}
        inputProps={{
          inputMode: "decimal",
          pattern: "-?[0-9]*[.,]?[0-9]*",
        }}
        onChange={handleChange}
        onKeyDownCapture={(e) => e.stopPropagation()} // ★ 關鍵：capture 階段
        onKeyDown={(e) => e.stopPropagation()}        // ★ 關鍵：bubble 階段
      />
    );
  },

  // 提交（按 Enter/Blur）時再把字串轉成數字
  // valueParser: (v) => toNumberSafe(v) ?? 0,
},

      {
        field: "curr",
        headerName: "幣別",
        minWidth: 75,
        flex: 0.5,
        type: "singleSelect",
        editable: canEditDetail,
        valueOptions: ["TWD", "USD", "PHP", "CNY", "HKD", "AUD", "EUR"], // 下拉選單
        valueFormatter: (p) => {
          const value = String(p.value ?? "").trim();
          return value || "未設定";
        },
      },
      { field: "pricePerson", headerName: "調價人員", minWidth: 90, flex: 0.8 },
      ...(canEditDetail ? [{
        field: "op",
        headerName: "操作",
        sortable: false,
        filterable: false,
        minWidth: 120,
        flex: 0.6,
        renderCell: (p) => (
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => deleteDetail(p.row)}
          >
            刪除
          </Button>
        ),
      }] : []),
    ],
    [canEditDetail]
  );

  const onDetailProcessRowUpdate = (newRow, oldRow) => {
    if (oldRow?.price !== newRow?.price || oldRow?.curr !== newRow?.curr) {
      setPendingDetailEdit(newRow);
    }
    return newRow;
  };
  

  // ------ 編輯對話框提交 ------
  const onSubmitEdit = async (evt) => {
    evt.preventDefault();
    if (!editItem || savingQuote.current) return;
    const form = new FormData(evt.currentTarget);
    const payload = {
      id: editItem.id,
      drawNum: quoteItem?.drawNum,
      vendorId: quoteVendor?.id,
      price: form.get("price")?.toString().trim(),
      engineering: form.get("engineering")?.toString().trim(),
      choose: form.get("choose")?.toString(),
      currency: form.get("currency")?.toString() || "TWD",
    };
    if (!payload.drawNum || !payload.vendorId || !payload.price) {
      alert("請從料品表與廠商資料表選擇圖號、廠商，並輸入進價");
      return;
    }
    const parsedPrice = toNumberSafe(payload.price);

    if (parsedPrice == null) {
      alert("進價請輸入整數、小數或負數");
      return;
    }

    payload.price = parsedPrice;
    savingQuote.current = true;
    setLoading(true);
    try {
      const resp = await apiClient.post(`/PurchasePrice/SaveQuote`, payload);
      if (resp.data?.success ?? resp.data?.Success) {
        alert(resp.data.message ?? resp.data.Message);
        setEditOpen(false);
        const saved = resp.data.data ?? resp.data.Data;
        setDrawNum(saved.drawNum);
        setStartDrawNum(saved.drawNum);
        setEndDrawNum(saved.drawNum);
        setCompany("");
        setItemName("");
        setPendingDetailEdit(null);
        await fetchMain({ drawNum: saved.drawNum, endDrawNum: saved.drawNum, itemName: "", company: "" });
        await fetchDetail(saved.drawNum, saved.vendor, saved.id);
      } else {
        alert("修改失敗，請確認欄位");
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message ?? err.response?.data?.Message ?? "儲存失敗，請稍後再試");
    } finally {
      savingQuote.current = false;
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* 遮罩 */}
      <Backdrop sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* 查詢列 */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          進價一覽表（查詢）
        </Typography>
        <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={3}>
        <Grid container spacing={1}>
        <Grid item xs={6}>
          <TextField
            label="起始圖號"
            value={startDrawNum}
            onChange={(e) => {
              setStartDrawNum(e.target.value);
              // 保留你原本的行為：同步到舊的 drawNum 以相容其他程式
              setDrawNum(e.target.value);
            }}
            onKeyDown={(e) => {
              // 避免輸入法組字時誤判 Enter
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                commitStartToEnd(e.currentTarget.value);
              }
            }}
            onBlur={() => {
              // 每次離開起始圖號時，都以目前起始圖號覆蓋結束圖號
              commitStartToEnd(startDrawNum);
            }}
            fullWidth
            size="small"
          />
        </Grid>
    <Grid item xs={6}>
      <TextField
        inputRef={endDrawNumRef}
        label="結束圖號"
        value={endDrawNum}
        onChange={(e) => setEndDrawNum(e.target.value)}
        fullWidth
        size="small"
      />
    </Grid>
  </Grid>
</Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="物品名"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="廠商"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={fetchMain}>
              查詢
            </Button>
            <Button variant="outlined" onClick={exportListExcel}>
              匯出一覽表 Excel
            </Button>
            <Button variant="outlined" color="secondary" onClick={exportSingleExcel}>
              匯出單筆調價紀錄 Excel
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2} columns={{ xs: 12, md: 14 }}>
  {/* 左 70%：主表 */}
  <Grid item xs={12} md={9}>
    <Typography variant="h6" sx={{ mb: 1 }}>
      廠商報價
    </Typography>
    <Box sx={{ height: 520, width: "100%" }}>
      <DataGrid
        rows={mainRows}
        columns={mainColumns}
        disableRowSelectionOnClick
        onRowDoubleClick={(params) => fetchDetail(params.row.drawNum, params.row.company, params.row.id)}
        rowHeight={38}
        headerHeight={32}
        getRowHeight={(params) => {
          const len = params.model?.itemName?.length ?? 0;
          return len > 30 ? 72 : 38;
        }}
        localeText={{ noRowsLabel: "尚未搜尋或查無資料" }}
      />
    </Box>
  </Grid>

  {/* 右 30%：副表 */}
  <Grid item xs={12} md={5}>
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
    <Typography variant="h6" sx={{ mb: 1 }}>
      進價紀錄 {selectedDrawNum ? `（圖號：${selectedDrawNum}／廠商：${selectedVendor}）` : ""}
    </Typography>
    {canEditDetail && (
      <Button
        variant="contained"
        size="small"
        color="primary"
        onClick={saveEditedDetail}
        disabled={!pendingDetailEdit}   // 沒有變更就不能按
      >
        保存
      </Button>
    )}
  </Box>
    <Box sx={{ height: 520, width: "100%" }}>
      <DataGrid
        rows={detailRows}
        columns={detailColumns}
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: "尚未選擇圖號或此圖號無紀錄" }}
        rowHeight={38}
        headerHeight={32}
        processRowUpdate={canEditDetail ? onDetailProcessRowUpdate : undefined}
        onProcessRowUpdateError={(e) => alert(e?.message ?? "儲存失敗")}
      />
    </Box>
  </Grid>
</Grid>


      {/* 編輯小視窗 */}
      <Dialog open={editOpen} onClose={() => { if (!savingQuote.current) setEditOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>新增廠商報價／調價</DialogTitle>
        <Box component="form" onSubmit={onSubmitEdit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <QuoteLookup kind="item" label="圖號（料品表）" value={quoteItem} onChange={setQuoteItem} />
              </Grid>
              <Grid item xs={12} md={6}>
                <QuoteLookup kind="vendor" label="廠商（廠商資料表）" value={quoteVendor} onChange={setQuoteVendor} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="price"
                  label="進價"
                  fullWidth
                  defaultValue={editItem?.price ?? ""}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="engineering"
                  label="工程內容"
                  fullWidth
                  defaultValue={editItem?.engineering ?? ""}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="currency"
                  label="幣別"
                  select
                  fullWidth
                  defaultValue={editItem?.currency ?? "TWD"}
                  size="small"
                >
                  <MenuItem value="TWD">TWD</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="CNY">CNY</MenuItem>
                  <MenuItem value="PHP">PHP</MenuItem>
                  <MenuItem value="HKD">HKD</MenuItem>
                  <MenuItem value="AUD">AUD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="choose"
                  label="選"
                  fullWidth
                  defaultValue={editItem?.choose ?? ""}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  變更圖號或廠商：保留原報價，新增報價及第一筆進價紀錄。
                  相同圖號與廠商：更新報價並新增調價紀錄；未變更不重複新增。
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button disabled={loading} onClick={() => setEditOpen(false)}>取消</Button>
            <Button disabled={loading || !quoteItem || !quoteVendor?.id} type="submit" variant="contained">儲存</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
